import { ApiError } from "@hakili/api-client";
import { t } from "@hakili/i18n";
import { Card } from "@hakili/ui";
import { AlertTriangle, Camera, CheckCircle2, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { depositFailureWords, type DepositLinkView } from "../session/documents.js";

/**
 * `round C7`, `C7.3`: **the page behind the deposit link** , WF-CON-06 step 3
 * (*"the link opens the camera on one named document, no login"*) and
 * WF-CLI-18. No drawing exists for it (`hakili-client.jsx` has no deposit
 * screen), so the page is the record's words and the client shell's column,
 * with no top bar and no tabs (6.3.6: the holder has no session).
 *
 * **The token is the credential.** It travels on the path to the three public
 * doors and is printed nowhere , not in the page, not in a log line, not in
 * a `data-` attribute a screenshot would carry.
 *
 * **A link that cannot be used is answered in words**: spent, expired, or
 * unknown, each its own sentence, never a blank page and never a `404`.
 */

export type DepositPageState =
  | { readonly kind: "reading" }
  | { readonly kind: "open"; readonly link: DepositLinkView }
  | { readonly kind: "sending"; readonly link: DepositLinkView }
  | { readonly kind: "received"; readonly link: DepositLinkView }
  | { readonly kind: "used" }
  | { readonly kind: "expired" }
  | { readonly kind: "unknown" }
  | { readonly kind: "readFailed"; readonly message: string }
  | { readonly kind: "refused"; readonly link: DepositLinkView; readonly message: string };

const stateOf = (link: DepositLinkView): DepositPageState => {
  if (link.state === "ouvert") return { kind: "open", link };
  if (link.state === "utilise") return { kind: "used" };
  return { kind: "expired" };
};

/** A failed READ says what the door said, else the network's own sentence: never the upload's « Le fichier n'a pas pu être envoyé ». */
const readFailureWords = (failure: unknown): string =>
  failure instanceof ApiError && failure.message !== "" ? failure.message : t("common.error.reseau");

/** `PIECES-COUNT`: the three answers the catalogue words; a pièce asking for more than three offers these three (the client app's, #840). */
const MONTHS_WORDS: ReadonlyMap<
  number,
  "client.dossier.moisUn" | "client.dossier.moisDeux" | "client.dossier.moisTrois"
> = new Map([
  [1, "client.dossier.moisUn"],
  [2, "client.dossier.moisDeux"],
  [3, "client.dossier.moisTrois"],
]);

export function DepositPage({
  read,
  deposit,
}: {
  /** Reads the one pièce the link names; rejects with the door's 404 when the link is unknown. */
  readonly read: () => Promise<DepositLinkView>;
  /** `declaredMonths` is passed only for a pièce that asks for several documents, once the visitor said how many months the file holds. */
  readonly deposit: (file: File, declaredMonths?: number) => Promise<DepositLinkView>;
}): React.JSX.Element {
  const [state, setState] = useState<DepositPageState>({ kind: "reading" });
  const [attempt, setAttempt] = useState(0);
  /** `DEPOSIT-LINK-MONTHS`: the file chosen for a multi-file pièce, held until its months are said. */
  const [pending, setPending] = useState<{ file: File; months: number | null } | null>(null);
  const input = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    let live = true;
    read()
      .then((link) => {
        if (live) setState(stateOf(link));
      })
      .catch((failure: unknown) => {
        if (!live) return;
        // The door's own 404 is « unknown » (a link that does not exist); any other failed read (the network, a gateway) is NOT a verdict on the link.
        setState(
          failure instanceof ApiError && failure.status === 404
            ? { kind: "unknown" }
            : { kind: "readFailed", message: readFailureWords(failure) },
        );
      });
    return () => {
      live = false;
    };
  }, [read, attempt]);

  const send = (file: File, link: DepositLinkView, declaredMonths?: number): void => {
    setState({ kind: "sending", link });
    setPending(null);
    deposit(file, declaredMonths)
      .then((after) => {
        setState(after.state === "utilise" ? { kind: "received", link: after } : stateOf(after));
      })
      .catch((failure: unknown) => {
        setState({
          kind: "refused",
          link,
          message: depositFailureWords(failure),
        });
      });
  };

  return (
    <div className="flex flex-col gap-4 pt-2">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-gray-900">{t("client.depot.titre")}</h1>
        <p className="mt-1 text-sm text-gray-500">{t("client.depot.resume")}</p>
      </div>

      {state.kind === "reading" ? (
        <p className="text-sm text-gray-500" data-testid="depot-lecture">
          {t("client.depot.chargement")}
        </p>
      ) : null}

      {state.kind === "readFailed" ? (
        <div
          role="alert"
          data-testid="depot-lecture-echec"
          className="flex flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4"
        >
          <p className="flex items-start gap-2 text-sm text-amber-700">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            {state.message}
          </p>
          <button
            type="button"
            data-testid="depot-reessayer"
            onClick={() => {
              setState({ kind: "reading" });
              setAttempt((count) => count + 1);
            }}
            className="self-start rounded-xl border border-amber-300 bg-white px-3 py-2 text-xs font-medium text-gray-700"
          >
            {t("common.action.reessayer")}
          </button>
        </div>
      ) : null}
      {state.kind === "unknown" ? (
        <Card shape="client">
          <p role="alert" className="text-sm text-gray-700" data-testid="depot-inconnu">
            {t("client.depot.inconnu")}
          </p>
        </Card>
      ) : null}
      {state.kind === "expired" ? (
        <Card shape="client">
          <p role="alert" className="text-sm text-gray-700" data-testid="depot-expire">
            {t("client.depot.expire")}
          </p>
        </Card>
      ) : null}
      {state.kind === "used" ? (
        <Card shape="client">
          <p className="text-sm text-gray-700" data-testid="depot-utilise">
            {t("client.depot.dejaUtilise")}
          </p>
        </Card>
      ) : null}

      {state.kind === "open" || state.kind === "sending" || state.kind === "refused" ? (
        <Card shape="client" className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <Upload className="h-5 w-5 shrink-0 text-indigo-600" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <p className="text-xs text-gray-500">{t("client.depot.pieceDemandee")}</p>
              <p className="text-base font-semibold text-gray-900" data-testid="depot-piece">
                {state.link.label}
              </p>
            </div>
          </div>
          <input
            ref={input}
            type="file"
            accept="image/*,application/pdf"
            className="hidden"
            data-testid="depot-fichier"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = "";
              if (file === undefined || state.kind === "sending") return;
              // A pièce that asks for several documents waits for the months of this file; any other sends at once and says nothing of months.
              if (state.link.multiFile) setPending({ file, months: null });
              else send(file, state.link);
            }}
          />
          {pending === null ? (
            <button
              type="button"
              data-testid="depot-photographier"
              disabled={state.kind === "sending"}
              onClick={() => {
                input.current?.click();
              }}
              className="flex w-full items-center justify-center gap-2 rounded-2xl bg-indigo-600 py-3.5 text-sm font-medium text-white disabled:opacity-50"
            >
              <Camera className="h-4 w-4" aria-hidden="true" />
              {state.kind === "sending" ? t("client.depot.envoiEnCours") : t("client.depot.photographier")}
            </button>
          ) : null}
          {pending === null ? (
            <p className="text-center text-xs text-gray-500">{t("client.depot.choisirFichier")}</p>
          ) : null}
          {pending === null ? null : (
            <div className="flex flex-col gap-3 border-t border-gray-100 pt-3">
              <fieldset className="m-0 min-w-0 border-0 p-0" data-testid="depot-mois">
                <legend className="mb-1 text-xs font-medium text-gray-900">
                  <span className="break-all text-gray-500">{pending.file.name}</span>
                  <span className="block">{t("client.dossier.moisDuFichier")}</span>
                </legend>
                <div className="flex flex-wrap gap-2">
                  {Array.from(
                    { length: Math.min(state.link.expectedFiles ?? 3, 3) },
                    (_, index) => index + 1,
                  ).map((count) => (
                    <label
                      key={count}
                      className="inline-flex cursor-pointer items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs text-gray-900 has-[:checked]:border-indigo-600 has-[:checked]:bg-indigo-50"
                    >
                      <input
                        type="radio"
                        className="accent-indigo-600"
                        name="depot-mois"
                        checked={pending.months === count}
                        disabled={state.kind === "sending"}
                        data-testid={`depot-mois-${String(count)}`}
                        onChange={() => {
                          setPending({ file: pending.file, months: count });
                        }}
                      />
                      {t(MONTHS_WORDS.get(count) ?? "client.dossier.moisUn")}
                    </label>
                  ))}
                </div>
              </fieldset>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  data-testid="depot-envoyer-fichier"
                  disabled={state.kind === "sending" || pending.months === null}
                  onClick={() => {
                    if (pending.months !== null) send(pending.file, state.link, pending.months);
                  }}
                  className="rounded-xl bg-indigo-600 px-3 py-2 text-xs font-medium text-white disabled:opacity-50"
                >
                  {state.kind === "sending" ? t("client.depot.envoiEnCours") : t("client.dossier.envoyer")}
                </button>
                <button
                  type="button"
                  data-testid="depot-annuler"
                  disabled={state.kind === "sending"}
                  onClick={() => {
                    setPending(null);
                  }}
                  className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 disabled:opacity-50"
                >
                  {t("common.action.annuler")}
                </button>
              </div>
              {pending.months === null ? (
                <p className="m-0 text-xs text-gray-500" data-testid="depot-mois-consigne">
                  {t("client.dossier.moisConsigne")}
                </p>
              ) : null}
            </div>
          )}
          <div className="flex items-start gap-2 rounded-xl bg-gray-50 px-3 py-2">
            <Camera className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden="true" />
            <p className="text-xs leading-relaxed text-gray-500">{t("client.dossier.conseilPhoto")}</p>
          </div>
          {state.kind === "refused" ? (
            <p role="alert" className="text-xs text-rose-700" data-testid="depot-refus">
              {state.message}
            </p>
          ) : null}
        </Card>
      ) : null}

      {state.kind === "received" ? (
        <Card shape="client" className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-6 w-6 shrink-0 text-emerald-500" aria-hidden="true" />
            <p className="text-base font-semibold text-gray-900" data-testid="depot-merci">
              {t("client.depot.merci")}
            </p>
          </div>
          <p className="text-sm text-gray-600">{state.link.label}</p>
          <p className="text-xs text-gray-500">
            {t(
              state.link.supplier === "vendeur"
                ? "client.depot.merciDetailVendeur"
                : "client.depot.merciDetail",
            )}
          </p>
        </Card>
      ) : null}
    </div>
  );
}
